import { useState } from 'react';
import { motion } from 'framer-motion';
import Navbar from '../../components/feature/Navbar';
import Footer from '../../components/feature/Footer';

export default function ContactPage() {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    subject: '',
    message: '',
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle');

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    });
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitStatus('idle');

    try {
      const formBody = new URLSearchParams();
      Object.entries(formData).forEach(([key, value]) => {
        formBody.append(key, value);
      });

      const response = await fetch('https://readdy.ai/api/form/d597to13kamldd1565j0', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: formBody.toString(),
      });

      if (response.ok) {
        setSubmitStatus('success');
        setFormData({ name: '', email: '', subject: '', message: '' });
      } else {
        setSubmitStatus('error');
      }
    } catch (error) {
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  const contactInfo = [
    {
      icon: 'ri-mail-line',
      title: 'Email',
      content: 'contact@pixel-anim.com',
      link: 'mailto:contact@pixel-anim.com',
      color: 'from-primary-blue to-purple-600',
    },
    {
      icon: 'ri-time-line',
      title: 'Horaires',
      content: 'Lun - Ven : 9h - 18h',
      color: 'from-primary-mint to-green-600',
    },
    {
      icon: 'ri-customer-service-line',
      title: 'Support',
      content: 'Réponse sous 24h',
      color: 'from-primary-yellow to-orange-500',
    },
  ];

  return (
    <div className="min-h-screen bg-white">
      <Navbar />

      {/* Hero Section */}
      <section className="pt-32 pb-16 bg-gradient-to-br from-blue-50 via-purple-50 to-pink-50">
        <div className="max-w-7xl mx-auto px-6">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="text-center"
          >
            <h1 className="text-6xl font-fredoka font-bold text-gray-900 mb-6">
              Contactez{' '}
              <span className="bg-gradient-to-r from-primary-blue via-purple-600 to-primary-coral bg-clip-text text-transparent">
                Nous
              </span>
            </h1>
            <p className="text-xl font-quicksand text-gray-600 max-w-3xl mx-auto">
              Une question ? Un projet spécial ? Notre équipe est là pour vous aider à créer votre magie
            </p>
          </motion.div>
        </div>
      </section>

      {/* Contact Info Cards */}
      <section className="py-16 -mt-8">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {contactInfo.map((info, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                className="bg-white rounded-3xl p-6 shadow-soft hover:shadow-soft-lg transition-all duration-300 border border-gray-100"
              >
                <div className={`w-14 h-14 flex items-center justify-center bg-gradient-to-br ${info.color} rounded-2xl mb-4`}>
                  <i className={`${info.icon} text-2xl text-white`}></i>
                </div>
                <h3 className="text-lg font-fredoka font-bold text-gray-900 mb-2">
                  {info.title}
                </h3>
                {info.link ? (
                  <a
                    href={info.link}
                    className="font-quicksand text-gray-600 hover:text-primary-blue transition-colors cursor-pointer"
                  >
                    {info.content}
                  </a>
                ) : (
                  <p className="font-quicksand text-gray-600">{info.content}</p>
                )}
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Contact Form Section */}
      <section className="py-24">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-start">
            {/* Left - Pixel Image */}
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
            >
              <div className="sticky top-32">
                <div className="w-full h-[500px] flex items-center justify-center mb-6">
                  <img
                    src="https://public.readdy.ai/ai/img_res/33ec47028c1e3d4317fd309dce0ca8f9.jpg"
                    alt="Pixel vous accueille"
                    className="w-full h-full object-contain drop-shadow-2xl"
                  />
                </div>
                <div className="bg-gradient-to-br from-primary-mint/20 to-green-100 rounded-3xl p-8">
                  <h3 className="text-2xl font-fredoka font-bold text-gray-900 mb-4">
                    Pixel est là pour vous aider !
                  </h3>
                  <p className="font-quicksand text-gray-700 leading-relaxed">
                    Notre équipe répond à toutes vos questions dans les plus brefs délais. N'hésitez pas à nous contacter pour discuter de votre projet ou pour toute demande d'information.
                  </p>
                </div>
              </div>
            </motion.div>

            {/* Right - Contact Form */}
            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
            >
              <div className="bg-white rounded-4xl p-8 shadow-soft border border-gray-100">
                <h2 className="text-3xl font-fredoka font-bold text-gray-900 mb-6">
                  Envoyez-nous un message
                </h2>

                <form id="contact-form" data-readdy-form onSubmit={handleSubmit} className="space-y-6">
                  {/* Name */}
                  <div>
                    <label htmlFor="name" className="block font-quicksand font-semibold text-gray-700 mb-2">
                      Nom complet *
                    </label>
                    <input
                      type="text"
                      id="name"
                      name="name"
                      value={formData.name}
                      onChange={handleChange}
                      required
                      className="w-full px-4 py-3 border-2 border-gray-200 rounded-2xl font-quicksand text-gray-900 focus:border-primary-blue focus:outline-none transition-colors text-sm"
                      placeholder="Votre nom"
                    />
                  </div>

                  {/* Email */}
                  <div>
                    <label htmlFor="email" className="block font-quicksand font-semibold text-gray-700 mb-2">
                      Email *
                    </label>
                    <input
                      type="email"
                      id="email"
                      name="email"
                      value={formData.email}
                      onChange={handleChange}
                      required
                      className="w-full px-4 py-3 border-2 border-gray-200 rounded-2xl font-quicksand text-gray-900 focus:border-primary-blue focus:outline-none transition-colors text-sm"
                      placeholder="votre@email.com"
                    />
                  </div>

                  {/* Subject */}
                  <div>
                    <label htmlFor="subject" className="block font-quicksand font-semibold text-gray-700 mb-2">
                      Sujet *
                    </label>
                    <select
                      id="subject"
                      name="subject"
                      value={formData.subject}
                      onChange={handleChange}
                      required
                      className="w-full px-4 py-3 border-2 border-gray-200 rounded-2xl font-quicksand text-gray-900 focus:border-primary-blue focus:outline-none transition-colors text-sm cursor-pointer"
                    >
                      <option value="">Sélectionnez un sujet</option>
                      <option value="question">Question générale</option>
                      <option value="commande">Question sur une commande</option>
                      <option value="personnalisation">Demande de personnalisation</option>
                      <option value="entreprise">Solution entreprise</option>
                      <option value="autre">Autre</option>
                    </select>
                  </div>

                  {/* Message */}
                  <div>
                    <label htmlFor="message" className="block font-quicksand font-semibold text-gray-700 mb-2">
                      Message *
                    </label>
                    <textarea
                      id="message"
                      name="message"
                      value={formData.message}
                      onChange={handleChange}
                      required
                      maxLength={500}
                      rows={6}
                      className="w-full px-4 py-3 border-2 border-gray-200 rounded-2xl font-quicksand text-gray-900 focus:border-primary-blue focus:outline-none transition-colors resize-none text-sm"
                      placeholder="Décrivez votre demande..."
                    ></textarea>
                    <p className="text-xs font-quicksand text-gray-500 mt-2">
                      Maximum 500 caractères ({formData.message.length}/500)
                    </p>
                  </div>

                  {/* Submit Button */}
                  <button
                    type="submit"
                    disabled={isSubmitting}
                    className="w-full px-8 py-4 bg-gradient-to-r from-primary-blue to-purple-600 text-white font-quicksand font-bold rounded-full hover:shadow-colored-blue transition-all duration-300 whitespace-nowrap cursor-pointer flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {isSubmitting ? (
                      <>
                        <i className="ri-loader-4-line animate-spin"></i>
                        <span>Envoi en cours...</span>
                      </>
                    ) : (
                      <>
                        <i className="ri-send-plane-fill"></i>
                        <span>Envoyer le message</span>
                      </>
                    )}
                  </button>

                  {/* Status Messages */}
                  {submitStatus === 'success' && (
                    <motion.div
                      initial={{ opacity: 0, y: -10 }}
                      animate={{ opacity: 1, y: 0 }}
                      className="p-4 bg-primary-mint/20 border-2 border-primary-mint rounded-2xl"
                    >
                      <div className="flex items-center gap-3">
                        <i className="ri-checkbox-circle-fill text-primary-mint text-2xl"></i>
                        <p className="font-quicksand text-gray-800">
                          Merci ! Votre message a été envoyé avec succès. Nous vous répondrons dans les plus brefs délais.
                        </p>
                      </div>
                    </motion.div>
                  )}

                  {submitStatus === 'error' && (
                    <motion.div
                      initial={{ opacity: 0, y: -10 }}
                      animate={{ opacity: 1, y: 0 }}
                      className="p-4 bg-red-50 border-2 border-red-300 rounded-2xl"
                    >
                      <div className="flex items-center gap-3">
                        <i className="ri-error-warning-fill text-red-500 text-2xl"></i>
                        <p className="font-quicksand text-gray-800">
                          Une erreur s'est produite. Veuillez réessayer ou nous contacter directement par email.
                        </p>
                      </div>
                    </motion.div>
                  )}
                </form>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Alternative Contact Methods */}
      <section className="py-24 bg-neutral-lighter">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-fredoka font-bold text-gray-900 mb-4">
              Autres moyens de nous contacter
            </h2>
            <p className="text-lg font-quicksand text-gray-600">
              Choisissez le canal qui vous convient le mieux
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 max-w-4xl mx-auto">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5 }}
              className="bg-white rounded-3xl p-8 shadow-soft hover:shadow-soft-lg transition-all duration-300 border border-gray-100"
            >
              <div className="w-16 h-16 flex items-center justify-center bg-gradient-to-br from-primary-blue to-purple-600 rounded-2xl mb-6 mx-auto">
                <i className="ri-chat-smile-3-line text-3xl text-white"></i>
              </div>
              <h3 className="text-2xl font-fredoka font-bold text-gray-900 mb-3 text-center">
                Chat en direct
              </h3>
              <p className="font-quicksand text-gray-600 text-center mb-6">
                Discutez avec notre équipe en temps réel pour une réponse immédiate
              </p>
              <button className="w-full px-6 py-3 bg-gray-900 text-white font-quicksand font-semibold rounded-full hover:bg-gray-800 transition-all duration-300 whitespace-nowrap cursor-pointer">
                Démarrer le chat
              </button>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: 0.1 }}
              className="bg-white rounded-3xl p-8 shadow-soft hover:shadow-soft-lg transition-all duration-300 border border-gray-100"
            >
              <div className="w-16 h-16 flex items-center justify-center bg-gradient-to-br from-primary-coral to-pink-600 rounded-2xl mb-6 mx-auto">
                <i className="ri-question-answer-line text-3xl text-white"></i>
              </div>
              <h3 className="text-2xl font-fredoka font-bold text-gray-900 mb-3 text-center">
                FAQ
              </h3>
              <p className="font-quicksand text-gray-600 text-center mb-6">
                Trouvez rapidement des réponses aux questions les plus fréquentes
              </p>
              <a
                href="/faq"
                className="block w-full px-6 py-3 bg-gray-900 text-white font-quicksand font-semibold rounded-full hover:bg-gray-800 transition-all duration-300 whitespace-nowrap cursor-pointer text-center"
              >
                Consulter la FAQ
              </a>
            </motion.div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}